import * as React from 'react';

import type { ChatProfile, ThreadSummaryDto } from '@/api/chat-client';
import { Badge } from '@/components/ui/badge';
import { formatDateTime } from '@/routes/communication/chat-format';
import { cn } from '@/lib/utils';

// =============================================================================
// chat-thread-list.tsx — Panneau gauche : liste des fils (R15-COM-02).
// =============================================================================
// Liste triée par récence (autorité API). Chaque fil est un BOUTON natif (navigation clavier native,
// WCAG 2.1.1) ; le fil ouvert porte `aria-current="true"`. Le badge non-lus porte un LIBELLÉ TEXTE
// (« N non lus ») — la couleur/point n'est jamais le seul porteur d'information (WCAG 1.4.1). L'aperçu
// (`lastMessagePreview`) est du texte BRUT rendu échappé par React (`{...}`).

export interface ChatThreadListProps {
  readonly profile: ChatProfile;
  readonly threads: readonly ThreadSummaryDto[];
  readonly selectedThreadId: string | null;
  readonly onSelectThread: (threadId: string) => void;
  /** État de chargement (squelette accessible). */
  readonly isLoading: boolean;
}

export function ChatThreadList({
  profile,
  threads,
  selectedThreadId,
  onSelectThread,
  isLoading,
}: ChatThreadListProps): React.JSX.Element {
  if (isLoading) {
    return (
      <p role="status" className="p-4 text-sm text-muted-foreground">
        Chargement des conversations…
      </p>
    );
  }

  if (threads.length === 0) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        Aucune conversation à afficher.
      </p>
    );
  }

  return (
    <ul className="flex flex-col divide-y divide-border">
      {threads.map((thread) => {
        const isSelected = thread.threadId === selectedThreadId;
        const hasUnread = thread.unreadCount > 0;
        return (
          <li key={thread.threadId}>
            <button
              type="button"
              aria-current={isSelected ? true : undefined}
              onClick={() => onSelectThread(thread.threadId)}
              className={cn(
                'flex w-full flex-col gap-1 px-4 py-3 text-left text-sm',
                'hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isSelected ? 'bg-accent text-accent-foreground' : '',
              )}
            >
              <span className="flex items-center justify-between gap-2">
                {/* P-6 : côté admin on nomme l'ENTREPRISE ; le SIRET reste le repli (référentiel
                    muet) et l'infobulle dans les deux cas — c'est lui qu'on donne au support. */}
                <span
                  className="min-w-0 truncate font-medium"
                  title={profile === 'admin' ? thread.supplierSiret : undefined}
                >
                  {profile === 'admin'
                    ? (thread.supplierRaisonSociale ?? thread.supplierSiret)
                    : 'JeKo'}
                </span>
                {hasUnread ? (
                  <Badge variant="invited" withDot>
                    {thread.unreadCount} non lus
                  </Badge>
                ) : null}
              </span>
              <span className="truncate text-xs text-muted-foreground">
                Campagne {thread.campaignId}
              </span>
              <span className="truncate text-xs text-muted-foreground">
                {thread.lastMessagePreview ?? 'Aucun message'}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {thread.lastMessageAt !== null
                  ? formatDateTime(thread.lastMessageAt)
                  : formatDateTime(thread.lastActivityAt)}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
